import { API_CONFIG } from '../config/api';
import { AsyncJobStartResponse } from '../types/asyncJob';
import { ArchitectureSection, TOTAL_ARCHITECTURE_SECTIONS } from '../types/generation';
import { 
  ProjectItem, 
  ProjectResponse, 
  ProjectCreate, 
  ProjectUpdate, 
  ProjectFileResponse,
  ProjectType,
  ProjectSection,
  ProjectProgress,
  ProjectContextResponse,
  ProjectContextReferenceDocumentCreate,
  ProjectContextInstructionsCreate,
  ProjectContextUploadedDocumentType,
  ProjectStatus,
} from '../types/project';
import { UserSearchResult } from '../types/review';
import { logger } from '../utils/logger';

import { apiClient } from './apiClient';
import { startAndPollJob } from './jobPollingService';

interface ExecutionTrace {
  steps?: Array<Record<string, unknown>>;
}

export type ExecutionPlanResult = Array<Record<string, unknown>> | string[] | null;

export interface C4GenerationResponse {
  project_id: number;
  success: boolean;
  content: string | null;
  diagram_id: string | null;
  message: string | null;
  error_message?: string | null;
  execution_trace?: ExecutionTrace;
}

interface ProjectSectionsQueryOptions {
  selectedOnly?: boolean;
}

interface FinalDocumentSectionSelectionPayload {
  section_names: string[];
}

const DEFAULT_PROJECT_PROGRESS: ProjectProgress = {
  contextUploaded: false,
  contextReviewed: false,
  architectureGenerated: false,
  sectionsCompleted: 0,
  totalSections: TOTAL_ARCHITECTURE_SECTIONS,
};

const DEFAULT_PROJECT_REVIEW_SUMMARY = {
  review_id: null,
  status: 'not_submitted' as const,
  reviewer_count: 0,
  approver_count: 0,
  approver_missing: false,
  approver_names: [],
  approved_at: null,
  updated_at: null,
};

const DEFAULT_PROJECT_PUBLISH_SUMMARY = {
  is_published: false,
  can_publish: false,
  published_by: null,
  published_at: null,
};

const GENERATED_SECTION_PROJECT_STATUSES = new Set<string>([
  ProjectStatus.IN_REVIEW,
  ProjectStatus.COMPLETED,
  ProjectStatus.PUBLISHED,
]);
const PROJECT_SECTIONS_PATH_SEGMENT = 'sections';
const FINAL_DOCUMENT_SELECTION_PATH_SEGMENT = 'final-selection';
const SELECTED_ONLY_QUERY_PARAM = 'selected_only';
const TRUE_QUERY_PARAM_VALUE = 'true';

export class ProjectService {
  /**
   * Fetch all project items from the API
   * @return Promise with array of project items
   * @throws ApiError on request failure
   */
  public async getProjects(): Promise<ProjectItem[]> {
    const projects = await apiClient.get<ProjectResponse[]>(`${API_CONFIG.ENDPOINTS.PROJECTS}/get`);
    // Enhance each project with mock data for the UI and normalize field names
    return projects.map(project => this.normalizeProject(project));
  }

  /**
   * Fetch a project item by ID
   * @param projectId - The ID of the project to fetch
   * @return Promise with the project item
   * @throws ApiError on request failure
   */
  public async getProject(projectId: number): Promise<ProjectItem> {
    const project = await apiClient.get<ProjectResponse>(`${API_CONFIG.ENDPOINTS.PROJECTS}/get/${projectId}`);
    return this.normalizeProject(project);
  }

  /**
   * Create a new project item
   * @param projectData - The project data to create
   * @return Promise with the created project item
   * @throws ApiError on request failure
   */
  public async createProject(projectData: {
    title: string;
    description: string;
    projectType: ProjectType;
    projectLineOfBusiness?: string | null;
    originalProjectLink?: string;
  }): Promise<ProjectItem> {
    const createData: ProjectCreate = {
      title: projectData.title,
      description: projectData.description,
      project_type: projectData.projectType,
      status: 'context_upload',
      original_link: projectData.originalProjectLink || undefined,
    };

    if (projectData.projectLineOfBusiness !== undefined) {
      createData.line_of_business = projectData.projectLineOfBusiness;
    }
    
    const project = await apiClient.post<ProjectResponse>(`${API_CONFIG.ENDPOINTS.PROJECTS}/create`, createData);
    return this.normalizeProject(project);
  }

  /**
   * Update a project item
   * @param projectId - The ID of the project to update
   * @param updateData - The data to update
   * @return Promise with the updated project item
   * @throws ApiError on request failure
   */
  public async updateProject(projectId: number, updateData: {
    title?: string;
    description?: string;
    lineOfBusiness?: string | null;
    status?: string;
    progress?: ProjectProgress;
    bizzdesign_url?: string | null;
    ear_url?: string | null;
    ear_wiki_url?: string | null;
  }): Promise<ProjectItem> {
    const updatePayload: ProjectUpdate = {};
    if (updateData.title !== undefined) {
      updatePayload.title = updateData.title;
    }
    if (updateData.description !== undefined) {
      updatePayload.description = updateData.description;
    }
    if (Object.prototype.hasOwnProperty.call(updateData, 'lineOfBusiness')) {
      updatePayload.line_of_business = updateData.lineOfBusiness ?? null;
    }
    if (updateData.status !== undefined) {
      updatePayload.status = updateData.status;
    }
    if (updateData.progress !== undefined) {
      updatePayload.progress = updateData.progress;
    }
    if (Object.hasOwn(updateData, 'bizzdesign_url')) {
      updatePayload.bizzdesign_url = updateData.bizzdesign_url ?? null;
    }
    if (Object.hasOwn(updateData, 'ear_url')) {
      updatePayload.ear_url = updateData.ear_url ?? null;
    }
    if (Object.hasOwn(updateData, 'ear_wiki_url')) {
      updatePayload.ear_wiki_url = updateData.ear_wiki_url ?? null;
    }
    
    const project = await apiClient.put<ProjectResponse>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/update/${projectId}`, 
      updatePayload,
    );
    return this.normalizeProject(project);
  }

  /**
   * Update a project item's title (backward compatibility)
   * @param projectId - The ID of the project to update
   * @param title - The new title for the project
   * @return Promise with the updated project item
   * @throws ApiError on request failure
   */
  public async updateProjectTitle(projectId: number, title: string): Promise<ProjectItem> {
    return this.updateProject(projectId, { title });
  }

  /**
   * Delete a project item by ID
   * @param projectId - The ID of the project to delete
   * @return Promise that resolves when deletion is successful
   * @throws ApiError on request failure
   */
  public async deleteProject(projectId: number): Promise<void> {
    return apiClient.delete<void>(`${API_CONFIG.ENDPOINTS.PROJECTS}/delete/${projectId}`);
  }

  /**
   * Publish an approved project.
   * @param projectId - The ID of the project to publish
   * @return Promise with the published project item
   * @throws ApiError on request failure
   */
  public async publishProject(projectId: number): Promise<ProjectItem> {
    const project = await apiClient.post<ProjectResponse>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/publish/${projectId}`,
    );
    return this.normalizeProject(project);
  }

  /**
   * Get files associated with a project
   * @param projectId - The ID of the project
   * @return Promise with array of project files
   * @throws ApiError on request failure
   */
  public async getProjectFiles(projectId: number): Promise<ProjectFileResponse[]> {
    return apiClient.get<ProjectFileResponse[]>(`${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/files`);
  }

  /**
   * Upload a file to a project
   * @param projectId - The ID of the project
   * @param file - The file to upload
   * @return Promise with the uploaded file metadata
   * @throws ApiError on request failure
   */
  public async uploadFile(projectId: number, file: File): Promise<ProjectFileResponse> {
    return apiClient.uploadFile<ProjectFileResponse>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/files/add`,
      file,
    );
  }

  /**
   * Delete a specific file from a project
   * @param projectId - The ID of the project
   * @param fileId - The ID of the file to delete
   * @return Promise that resolves when deletion is successful
   * @throws ApiError on request failure
   */
  public async deleteFile(projectId: number, fileId: string): Promise<void> {
    return apiClient.delete<void>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/files/${fileId}`,
    );
  }

  /**
   * Delete all files from a project
   * @param projectId - The ID of the project
   * @return Promise that resolves when deletion is successful
   * @throws ApiError on request failure
   */
  public async deleteAllFiles(projectId: number): Promise<void> {
    return apiClient.delete<void>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/files`,
    );
  }

  // ====================
  // Context Management Methods
  // ====================

  /**
   * Get all context items for a project
   * @param projectId - The ID of the project
   * @return Promise with array of context items
   * @throws ApiError on request failure
   */
  public async getProjectContext(projectId: number): Promise<ProjectContextResponse[]> {
    return apiClient.get<ProjectContextResponse[]>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context`,
    );
  }

  /**
   * Upload a context document to a project
   * @param projectId - The ID of the project
   * @param file - The file to upload
   * @param documentType - The type of document being uploaded
   * @return Promise with the uploaded document metadata
   * @throws ApiError on request failure
   */
  public async uploadContextDocument(
    projectId: number, 
    file: File, 
    documentType: ProjectContextUploadedDocumentType,
  ): Promise<ProjectContextResponse> {
    const formData = new FormData();
    formData.append('file', file);
    
    return apiClient.postFormData<ProjectContextResponse>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/upload_document` +
      `?document_type=${documentType}`,
      formData,
    );
  }

  /**
   * Delete an uploaded context document from a project
   * @param projectId - The ID of the project
   * @param blobPath - The blob path of the document to delete
   * @return Promise that resolves when deletion is successful
   * @throws ApiError on request failure
   */
  public async deleteUploadedContextDocument(
    projectId: number, 
    blobPath: string,
  ): Promise<void> {
    return apiClient.delete<void>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/upload_document` +
      `?blob_path=${encodeURIComponent(blobPath)}`,
    );
  }

  /**
   * Get the content of an uploaded context document
   * @param projectId - The ID of the project
   * @param blobPath - The blob path of the document
   * @return Promise with the document content and metadata
   * @throws ApiError on request failure
   */
  public async getUploadedDocumentContent(
    projectId: number,
    blobPath: string,
  ): Promise<{
    filename: string;
    content: string;
    document_type: string;
    blob_path: string;
    updated_at: string | null;
    size: number;
  }> {
    return apiClient.get<{
      filename: string;
      content: string;
      document_type: string;
      blob_path: string;
      updated_at: string | null;
      size: number;
    }>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/document/content` +
      `?blob_path=${encodeURIComponent(blobPath)}`,
    );
  }

  /**
   * Add a reference document (external link) to a project
   * @param projectId - The ID of the project
   * @param fileUrl - The URL of the reference document
   * @return Promise with the created reference document
   * @throws ApiError on request failure
   */
  public async addReferenceDocument(projectId: number, fileUrl: string): Promise<ProjectContextResponse> {
    const data: ProjectContextReferenceDocumentCreate = {
      type: 'ProjectContextReferenceDocument',
      file_url: fileUrl,
    };
    
    return apiClient.post<ProjectContextResponse>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/ref_document`,
      data,
    );
  }

  /**
   * Delete a reference document from a project
   * @param projectId - The ID of the project
   * @param fileUrl - The URL of the reference document to delete
   * @return Promise that resolves when deletion is successful
   * @throws ApiError on request failure
   */
  public async deleteReferenceDocument(projectId: number, fileUrl: string): Promise<void> {
    return apiClient.delete<void>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/ref_document` +
      `?file_url=${encodeURIComponent(fileUrl)}`,
    );
  }

  /**
   * Add instructions/context to a project
   * @param projectId - The ID of the project
   * @param instructions - The instructions text to add
   * @return Promise with the created instructions
   * @throws ApiError on request failure
   */
  public async addInstructions(projectId: number, instructions: string): Promise<ProjectContextResponse> {
    const data: ProjectContextInstructionsCreate = {
      type: 'ProjectContextInstructions',
      instructions,
    };
    
    return apiClient.post<ProjectContextResponse>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/instructions`,
      data,
    );
  }

  /**
   * Save/update instructions for a project context
   * This method will either create new instructions or update existing ones
   * @param projectId - The ID of the project
   * @param instructions - The instructions text to save
   * @return Promise with the saved instructions
   * @throws ApiError on request failure
   */
  public async saveInstructions(projectId: number, instructions: string): Promise<ProjectContextResponse> {
    const data: ProjectContextInstructionsCreate = {
      type: 'ProjectContextInstructions',
      instructions,
    };
    
    // Use POST to create/update instructions - the backend should handle whether to create or update
    return apiClient.post<ProjectContextResponse>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/instructions`,
      data,
    );
  }

  /**
   * Add multiple reference documents from search results to a project
   * @param projectId - The ID of the project
   * @param fileUrls - Array of file URLs to add as reference documents
   * @return Promise with array of created reference documents
   * @throws ApiError on request failure
   */
  public async addMultipleReferenceDocuments(
    projectId: number, 
    fileUrls: string[],
  ): Promise<ProjectContextResponse[]> {
    const results: ProjectContextResponse[] = [];
    
    for (const fileUrl of fileUrls) {
      try {
        const result = await this.addReferenceDocument(projectId, fileUrl);
        results.push(result);
      } catch (error) {
        logger.error(`Failed to add reference document ${fileUrl}:`, error);
        // Continue with other files even if one fails
      }
    }
    
    return results;
  }

  /**
   * Delete multiple reference documents from a project
   * @param projectId - The ID of the project
   * @param fileUrls - Array of file URLs to delete
   * @return Promise that resolves when all deletions are complete
   * @throws ApiError on request failure
   */
  public async deleteMultipleReferenceDocuments(
    projectId: number, 
    fileUrls: string[],
  ): Promise<void> {
    for (const fileUrl of fileUrls) {
      try {
        await this.deleteReferenceDocument(projectId, fileUrl);
      } catch (error) {
        logger.error(`Failed to delete reference document ${fileUrl}:`, error);
        // Continue with other files even if one fails
      }
    }
  }

  /**
   * Helper function to convert ArchitectureSection enum to readable title
   * @param section - The architecture section enum value
   * @return Human-readable title for the section
   */
  private getSectionTitle(section: ArchitectureSection): string {
    const titleMap: Record<ArchitectureSection, string> = {
      [ArchitectureSection.BUSINESS_PURPOSE]: 'Business Purpose',
      [ArchitectureSection.SOLUTION_SUMMARY]: 'Solution Summary',
      [ArchitectureSection.ADR_SUMMARY]: 'Architecture Decision Records Summary',
      [ArchitectureSection.CONCEPTUAL_ARCHITECTURE]: 'Conceptual Architecture',
      [ArchitectureSection.DATA_ARCHITECTURE]: 'Data Architecture',
      [ArchitectureSection.INTEGRATION_ARCHITECTURE]: 'Integration Architecture',
      [ArchitectureSection.IMPACTED_COMPONENTS]: 'Impacted Components',
      [ArchitectureSection.SECURITY_ARCHITECTURE]: 'Security Architecture',
      [ArchitectureSection.TECHNOLOGY_ARCHITECTURE]: 'Technology Architecture',
      [ArchitectureSection.DEPLOYMENT_MODEL]: 'Deployment Model',
      [ArchitectureSection.RISK_ASSESSMENT]: 'Risk Assessment',
      [ArchitectureSection.THREAT_MODELING]: 'Threat Modeling',
    };
    return titleMap[section];
  }

  /**
   * Get project sections from the API and combine them in a single document
   * @param projectId - The ID of the project
   * @param options - Optional query flags for returned sections
   * @return Promise with array of sections containing title and markdown content
   * @throws ApiError on request failure
   */
  public async getSections(
    projectId: number,
    options?: ProjectSectionsQueryOptions,
  ): Promise<ProjectSection[]> {
    try {
      const searchParams = new URLSearchParams();
      if (options?.selectedOnly) {
        searchParams.set(SELECTED_ONLY_QUERY_PARAM, TRUE_QUERY_PARAM_VALUE);
      }
      const queryString = searchParams.toString();
      const sectionsEndpoint = `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/${PROJECT_SECTIONS_PATH_SEGMENT}${queryString ? `?${queryString}` : ''}`;

      // Fetch all sections from the API
      const sectionsResponse = await apiClient.get<Record<string, string>>(
        sectionsEndpoint,
      );

      // Map API response property names to ArchitectureSection enum values
      const apiToEnumMap: Record<string, ArchitectureSection> = {
        'business_purpose': ArchitectureSection.BUSINESS_PURPOSE,
        'solution_summary': ArchitectureSection.SOLUTION_SUMMARY,
        'adr_summary': ArchitectureSection.ADR_SUMMARY,
        'conceptual_architecture': ArchitectureSection.CONCEPTUAL_ARCHITECTURE,
        'data_architecture': ArchitectureSection.DATA_ARCHITECTURE,
        'integration_architecture': ArchitectureSection.INTEGRATION_ARCHITECTURE,
        'impacted_components': ArchitectureSection.IMPACTED_COMPONENTS,
        'security_architecture': ArchitectureSection.SECURITY_ARCHITECTURE,
        'technology_architecture': ArchitectureSection.TECHNOLOGY_ARCHITECTURE,
        'deployment_model': ArchitectureSection.DEPLOYMENT_MODEL,
        'risk_assessment': ArchitectureSection.RISK_ASSESSMENT,
        'threat_modeling': ArchitectureSection.THREAT_MODELING,
      };

      // Create a map of section content by ArchitectureSection enum for easy lookup
      const sectionMap = new Map<ArchitectureSection, string>();
      Object.entries(sectionsResponse).forEach(([apiKey, content]) => {
        const enumValue = apiToEnumMap[apiKey];
        if (enumValue && content?.trim()) {
          sectionMap.set(enumValue, content);
        }
      });

      // Combine sections in the order defined by ArchitectureSection enum
      const orderedSections = Object.values(ArchitectureSection);
      const combinedSections: ProjectSection[] = [];

      orderedSections.forEach(section => {
        const content = sectionMap.get(section);
        if (content?.trim()) {
          combinedSections.push({
            id: section,
            title: this.getSectionTitle(section),
            content: content.trim(),
          });
        }
      });

      return combinedSections;
    } catch (error) {
      logger.error('Failed to fetch project sections:', error);
      // Return empty array if API call fails
      return [];
    }
  }

  /**
   * Get the generated sections currently selected for the final document.
   * @param projectId - The ID of the project
   * @return Promise with selected section names in display order
   * @throws ApiError on request failure
   */
  public async getFinalDocumentSectionSelection(
    projectId: number,
  ): Promise<string[]> {
    const finalDocumentSelectionEndpoint = [
      API_CONFIG.ENDPOINTS.PROJECTS,
      projectId,
      PROJECT_SECTIONS_PATH_SEGMENT,
      FINAL_DOCUMENT_SELECTION_PATH_SEGMENT,
    ].join('/');

    const response = await apiClient.get<FinalDocumentSectionSelectionPayload>(
      finalDocumentSelectionEndpoint,
    );

    return response.section_names ?? [];
  }

  /**
   * Update which generated sections belong in the final document.
   * @param projectId - The ID of the project
   * @param sectionNames - Selected generated section names
   * @return Promise with the saved selected section names
   * @throws ApiError on request failure
   */
  public async updateFinalDocumentSectionSelection(
    projectId: number,
    sectionNames: string[],
  ): Promise<string[]> {
    const finalDocumentSelectionEndpoint = [
      API_CONFIG.ENDPOINTS.PROJECTS,
      projectId,
      PROJECT_SECTIONS_PATH_SEGMENT,
      FINAL_DOCUMENT_SELECTION_PATH_SEGMENT,
    ].join('/');

    const response = await apiClient.put<FinalDocumentSectionSelectionPayload>(
      finalDocumentSelectionEndpoint,
      { section_names: sectionNames },
    );

    return response.section_names ?? [];
  }

  /**
   * Get the count of completed sections for a project
   * @param projectId - The ID of the project
   * @return Promise with the number of completed sections
   * @throws ApiError on request failure
   */
  public async getSectionCount(projectId: number): Promise<number> {
    try {
      const sectionsResponse = await apiClient.get<Record<string, string>>(
        `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/sections`,
      );
      return Object.values(sectionsResponse).filter(v => v?.trim()).length;
    } catch (error) {
      logger.error('Failed to fetch section count:', error);
      return 0;
    }
  }

  /**
   * Save additional context fields as markdown files
   * @param projectId - The ID of the project
   * @param fields - Additional context fields (interfaces, cloud, security,
   * personas, database_technology)
   * @return Promise with the response containing saved filenames
   * @throws ApiError on request failure
   */
  public async saveAdditionalContextFields(
    projectId: number,
    fields: {
      interfaces?: string;
      cloud?: string;
      security?: string;
      personas?: string;
      database_technology?: string;
      other?: string;
    },
  ): Promise<{ saved_files: string[]; project_id: number }> {
    return apiClient.post<{ saved_files: string[]; project_id: number }>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/additional_fields`,
      {
        interfaces: fields.interfaces || '',
        cloud: fields.cloud || '',
        security: fields.security || '',
        personas: fields.personas || '',
        database_technology: fields.database_technology || '',
        other: fields.other || '',
      },
    );
  }

  /**
   * Get AI-generated context summaries for solution ideation
   * @param projectId - The ID of the project
   * @return Promise with AI-generated summaries for 6 categories
   * @throws ApiError on request failure
   */
  public async getContextSummaries(
    projectId: number,
  ): Promise<{
    business_solution: string;
    data_architecture: string;
    application_architecture: string;
    technical_architecture: string;
    information_security_architecture: string;
    whiteboarding_additional_context: string;
  }> {
    return apiClient.get<{
      business_solution: string;
      data_architecture: string;
      application_architecture: string;
      technical_architecture: string;
      information_security_architecture: string;
      whiteboarding_additional_context: string;
    }>(`${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/summaries`);
  }

  /**
   * Load additional context fields from blob storage
   * @param projectId - The ID of the project
   * @return Promise with the additional context fields
   * @throws ApiError on request failure
   */
  public async loadAdditionalContextFields(
    projectId: number,
  ): Promise<{
    interfaces: string;
    cloud: string;
    security: string;
    personas: string;
    database_technology: string;
    other: string;
  }> {
    return apiClient.get<{
      interfaces: string;
      cloud: string;
      security: string;
      personas: string;
      database_technology: string;
      other: string;
    }>(`${API_CONFIG.ENDPOINTS.PROJECTS}/${projectId}/context/additional_fields`);
  }

  /**
   * Generate a C4/Structurizr diagram using the AI agent
   * @param projectId - The ID of the project
   * @param userInput - Optional user input to guide the generation
   * @return Promise with the generated diagram response
   * @throws ApiError on request failure
   */
  public async generateC4Diagram(
    projectId: number,
    userInput?: string,
  ): Promise<C4GenerationResponse> {
    return startAndPollJob<C4GenerationResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `/api/c4-diagrams/${projectId}/generate/async`,
          {
            user_input:
              userInput ??
              'Generate a comprehensive C4 model based on all available ' +
                'project context including BRD, ADRs, and technical requirements.',
          },
        ),
    );
  }

  /**
   * Refine an existing C4/Structurizr diagram using the AI agent
   * @param projectId - The ID of the project
   * @param userFeedback - User feedback for refinement
   * @param currentDSL - The current Structurizr DSL content
   * @return Promise with the refined diagram response
   * @throws ApiError on request failure
   */
  public async refineC4Diagram(
    projectId: number,
    userFeedback: string,
    currentDSL: string,
  ): Promise<C4GenerationResponse> {
    return startAndPollJob<C4GenerationResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `/api/c4-diagrams/${projectId}/refine/async`,
          {
            user_feedback: userFeedback,
            current_dsl: currentDSL,
          },
        ),
    );
  }

  /**
   * Get execution plan for C4 diagram generation process
   * @param projectId - Project ID
   * @returns Promise with detailed execution plan
   */
  public async getC4GenerationExecutionPlan(
    projectId: number,
  ): Promise<ExecutionPlanResult> {
    try {
      return await apiClient.get<ExecutionPlanResult>(
        `/api/c4-diagrams/${projectId}/generate/execution_plan`,
      );
    } catch (error) {
      logger.error('Failed to get C4 generation execution plan:', error);
      return null;
    }
  }

  /**
   * Get execution plan for C4 diagram refinement process
   * @param projectId - Project ID
   * @returns Promise with detailed execution plan
   */
  public async getC4RefinementExecutionPlan(
    projectId: number,
  ): Promise<ExecutionPlanResult> {
    try {
      return await apiClient.get<ExecutionPlanResult>(
        `/api/c4-diagrams/${projectId}/refine/execution_plan`,
      );
    } catch (error) {
      logger.error('Failed to get C4 refinement execution plan:', error);
      return null;
    }
  }

  /**
   * Generate a technology architecture Structurizr diagram using the AI agent.
   * Translates the existing Mermaid technology architecture diagram into
   * Structurizr DSL.
   * @param projectId - The ID of the project
   * @param userInput - Optional user input to guide the translation
   * @return Promise with the generated diagram response
   * @throws ApiError on request failure
   */
  public async generateTechnologyDiagram(
    projectId: number,
    userInput?: string,
  ): Promise<C4GenerationResponse> {
    return startAndPollJob<C4GenerationResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `/api/technology-diagrams/${projectId}/generate/async`,
          {
            user_input:
              userInput ??
              'Translate the existing Mermaid technology architecture diagram ' +
                'into a valid Structurizr DSL workspace, preserving all nodes ' +
                'and relationships.',
          },
        ),
    );
  }

  /**
   * Refine an existing technology architecture Structurizr diagram using the
   * AI agent.
   * @param projectId - The ID of the project
   * @param userFeedback - User feedback for refinement
   * @param currentDSL - The current Structurizr DSL content
   * @return Promise with the refined diagram response
   * @throws ApiError on request failure
   */
  public async refineTechnologyDiagram(
    projectId: number,
    userFeedback: string,
    currentDSL: string,
  ): Promise<C4GenerationResponse> {
    return startAndPollJob<C4GenerationResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `/api/technology-diagrams/${projectId}/refine/async`,
          {
            user_feedback: userFeedback,
            current_dsl: currentDSL,
          },
        ),
    );
  }

  /**
   * Get execution plan for technology architecture diagram generation.
   * @param projectId - Project ID
   * @returns Promise with detailed execution plan
   */
  public async getTechnologyGenerationExecutionPlan(
    projectId: number,
  ): Promise<ExecutionPlanResult> {
    try {
      return await apiClient.get<ExecutionPlanResult>(
        `/api/technology-diagrams/${projectId}/generate/execution_plan`,
      );
    } catch (error) {
      logger.error(
        'Failed to get technology generation execution plan:',
        error,
      );
      return null;
    }
  }

  /**
   * Save a technology architecture Structurizr diagram for a project.
   * @param projectId - The ID of the project
   * @param diagramData - The diagram data to save
   * @return Promise with the saved diagram response
   * @throws ApiError on request failure
   */
  public async saveTechnologyDiagram(
    projectId: number,
    diagramData: {
      name: string;
      type: string;
      content: string;
      description?: string;
    },
  ): Promise<{
    id: string;
    name: string;
    type: string;
    content: string;
    description?: string;
    created_at: string;
    updated_at: string;
  }> {
    return apiClient.post<{
      id: string;
      name: string;
      type: string;
      content: string;
      description?: string;
      created_at: string;
      updated_at: string;
    }>(`/api/technology-diagrams/${projectId}`, diagramData);
  }

  /**
   * Generate an interface architecture Structurizr diagram using the AI agent.
   * Converts the C4 System Context diagram into an Interface Architecture view
   * whose relationships are labelled with data-flow Interface_IDs.
   * @param projectId - The ID of the project
   * @param userInput - Optional user input to guide the translation
   * @return Promise with the generated diagram response
   * @throws ApiError on request failure
   */
  public async generateInterfaceDiagram(
    projectId: number,
    userInput?: string,
  ): Promise<C4GenerationResponse> {
    return startAndPollJob<C4GenerationResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `/api/interface-diagrams/${projectId}/generate/async`,
          {
            user_input:
              userInput ??
              'Convert the existing C4 System Context diagram into an Interface ' +
                'Architecture Structurizr DSL view, labelling every relationship ' +
                'with the matching data-flow Interface_ID from the Integration ' +
                'Architecture table.',
          },
        ),
    );
  }

  /**
   * Refine an existing interface architecture Structurizr diagram using the
   * AI agent.
   * @param projectId - The ID of the project
   * @param userFeedback - User feedback for refinement
   * @param currentDSL - The current Structurizr DSL content
   * @return Promise with the refined diagram response
   * @throws ApiError on request failure
   */
  public async refineInterfaceDiagram(
    projectId: number,
    userFeedback: string,
    currentDSL: string,
  ): Promise<C4GenerationResponse> {
    return startAndPollJob<C4GenerationResponse>(
      projectId,
      () =>
        apiClient.post<AsyncJobStartResponse>(
          `/api/interface-diagrams/${projectId}/refine/async`,
          {
            user_feedback: userFeedback,
            current_dsl: currentDSL,
          },
        ),
    );
  }

  /**
   * Get execution plan for interface architecture diagram generation.
   * @param projectId - Project ID
   * @returns Promise with detailed execution plan
   */
  public async getInterfaceGenerationExecutionPlan(
    projectId: number,
  ): Promise<ExecutionPlanResult> {
    try {
      return await apiClient.get<ExecutionPlanResult>(
        `/api/interface-diagrams/${projectId}/generate/execution_plan`,
      );
    } catch (error) {
      logger.error(
        'Failed to get interface generation execution plan:',
        error,
      );
      return null;
    }
  }

  /**
   * Save an interface architecture Structurizr diagram for a project.
   * @param projectId - The ID of the project
   * @param diagramData - The diagram data to save
   * @return Promise with the saved diagram response
   * @throws ApiError on request failure
   */
  public async saveInterfaceDiagram(
    projectId: number,
    diagramData: {
      name: string;
      type: string;
      content: string;
      description?: string;
    },
  ): Promise<{
    id: string;
    name: string;
    type: string;
    content: string;
    description?: string;
    created_at: string;
    updated_at: string;
  }> {
    return apiClient.post<{
      id: string;
      name: string;
      type: string;
      content: string;
      description?: string;
      created_at: string;
      updated_at: string;
    }>(`/api/interface-diagrams/${projectId}`, diagramData);
  }

  /**
   * Save a C4/Structurizr diagram for a project
   * @param projectId - The ID of the project
   * @param diagramData - The diagram data to save
   * @return Promise with the saved diagram response
   * @throws ApiError on request failure
   */
  public async saveDiagram(
    projectId: number,
    diagramData: {
      name: string;
      type: string;
      content: string;
      description?: string;
    },
  ): Promise<{
    id: string;
    name: string;
    type: string;
    content: string;
    description?: string;
    created_at: string;
    updated_at: string;
  }> {
    return apiClient.post<{
      id: string;
      name: string;
      type: string;
      content: string;
      description?: string;
      created_at: string;
      updated_at: string;
    }>(
      `/api/c4-diagrams/${projectId}`,
      diagramData,
    );
  }

  /**
   * Fetch all diagrams for a project
   * @param projectId - The ID of the project
   * @return Promise with array of diagrams
   * @throws ApiError on request failure
   */
  public async getDiagrams(
    projectId: number,
  ): Promise<{
    id: string;
    name: string;
    type: string;
    content: string;
    description?: string;
    created_at: string;
    updated_at: string;
  }[]> {
    const response = await apiClient.get<{
      diagrams: Array<{
        id: string;
        name: string;
        type: string;
        description?: string;
        created_at: string;
        updated_at: string;
      }>;
      project_id: number;
    }>(`/api/c4-diagrams/${projectId}`);

    // For each diagram metadata, fetch the full content
    return Promise.all(
      (response.diagrams || []).map(async (metadata) => {
        try {
          return await this.getDiagramById(projectId, metadata.id);
        } catch {
          // If fetch fails, return metadata without content
          return {
            ...metadata,
            content: '',
          };
        }
      }),
    );
  }

  /**
   * Fetch a single diagram by ID
   * @param projectId - The ID of the project
   * @param diagramId - The ID of the diagram
   * @return Promise with the diagram
   * @throws ApiError on request failure
   */
  public async getDiagramById(
    projectId: number,
    diagramId: string,
  ): Promise<{
    id: string;
    name: string;
    type: string;
    content: string;
    description?: string;
    created_at: string;
    updated_at: string;
  }> {
    return apiClient.get<{
      id: string;
      name: string;
      type: string;
      content: string;
      description?: string;
      created_at: string;
      updated_at: string;
    }>(`/api/c4-diagrams/${projectId}/${diagramId}`);
  }

  /**
   * Lookup a user by email address
   * @param email - Email address to lookup
   * @return Promise with user information
   * @throws ApiError on request failure
   */
  public async lookupUserByEmail(email: string): Promise<{
    username: string;
    email: string;
    display_name?: string;
  }> {
    return apiClient.post('/api/sharing/lookup-user', { email });
  }

  /**
   * Search Archie users by partial email, username, or display name.
   * @param query - Search text entered by the user
   * @return Promise with matching registered Archie users
   */
  public async searchUsers(query: string): Promise<UserSearchResult[]> {
    const params = new URLSearchParams({ q: query });
    return apiClient.get<UserSearchResult[]>(
      `/api/sharing/search-users?${params.toString()}`,
    );
  }

  /**
   * Share a project with a collaborator
   * @param projectId - ID of the project to share
   * @param email - Email of the collaborator
   * @return Promise with sharing details
   * @throws ApiError on request failure
   */
  public async shareProject(
    projectId: number,
    email: string,
  ): Promise<{
    project_id: number;
    collaborator_username: string;
    collaborator_email: string;
    shared_at: string;
    message: string;
  }> {
    return apiClient.post(
      `/api/sharing/project/${projectId}/share`,
      { email },
    );
  }

  /**
   * Remove a collaborator from a project
   * @param projectId - ID of the project
   * @param username - Username of the collaborator to remove
   * @return Promise with removal confirmation
   * @throws ApiError on request failure
   */
  public async removeCollaborator(
    projectId: number,
    username: string,
  ): Promise<{
    project_id: number;
    removed_username: string;
    message: string;
  }> {
    return apiClient.delete(
      `/api/sharing/project/${projectId}/collaborator/${username}`,
    );
  }

  /**
   * Get all collaborators for a project
   * @param projectId - ID of the project
   * @return Promise with list of collaborators
   * @throws ApiError on request failure
   */
  public async getCollaborators(projectId: number): Promise<Array<{
    username: string;
    email: string;
    display_name?: string;
    line_of_business?: string | null;
    is_owner: boolean;
  }>> {
    return apiClient.get(`/api/sharing/project/${projectId}/collaborators`);
  }

  /**
   * Get shared projects (where user is co-owner)
   * @return Promise with array of shared projects
   * @throws ApiError on request failure
   */
  public async getSharedProjects(): Promise<ProjectItem[]> {
    const projects = await apiClient.get<ProjectResponse[]>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/get?filter_type=shared`,
    );
    return projects.map(project => this.normalizeProject(project));
  }

  /**
   * Get owned projects (where user is owner)
   * @return Promise with array of owned projects
   * @throws ApiError on request failure
   */
  public async getOwnedProjects(): Promise<ProjectItem[]> {
    const projects = await apiClient.get<ProjectResponse[]>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/get?filter_type=owned`,
    );
    return projects.map(project => this.normalizeProject(project));
  }

  /**
   * Get published projects (visible to all Archie users)
   * @return Promise with array of published projects
   * @throws ApiError on request failure
   */
  public async getPublishedProjects(): Promise<ProjectItem[]> {
    const projects = await apiClient.get<ProjectResponse[]>(
      `${API_CONFIG.ENDPOINTS.PROJECTS}/get?filter_type=published`,
    );
    return projects.map(project => this.normalizeProject(project));
  }

  /**
   * Normalize API response to match frontend expectations
   * @param project - Raw project response from API
   * @return Normalized project item
   */
  private normalizeProject(project: ProjectResponse): ProjectItem {
    const fallbackProgress = this.getFallbackProgress(project.status);
    const normalizedProgress: ProjectProgress = {
      ...fallbackProgress,
      ...(project.progress ?? {}),
      totalSections:
        project.progress?.totalSections ?? fallbackProgress.totalSections,
    };

    const normalized: ProjectItem = {
      ...project,
      progress: normalizedProgress,
      review_summary: {
        ...DEFAULT_PROJECT_REVIEW_SUMMARY,
        ...(project.review_summary ?? {}),
      },
      publish_summary: {
        ...DEFAULT_PROJECT_PUBLISH_SUMMARY,
        ...(project.publish_summary ?? {}),
        is_published:
          project.publish_summary?.is_published ??
          project.status === ProjectStatus.PUBLISHED,
      },
      projectType: project.project_type,
      projectLineOfBusiness: project.line_of_business ?? null,
      originalProjectLink: project.original_link || undefined,
    };
    
    return normalized;
  }

  /**
   * Build a safe fallback progress object when the API does not yet persist one.
   * @param status - The current project status
   * @return ProjectProgress used by the project list UI
   */
  private getFallbackProgress(status: ProjectStatus | string): ProjectProgress {
    if (GENERATED_SECTION_PROJECT_STATUSES.has(status)) {
      return {
        contextUploaded: true,
        contextReviewed: true,
        architectureGenerated: true,
        sectionsCompleted: DEFAULT_PROJECT_PROGRESS.totalSections,
        totalSections: DEFAULT_PROJECT_PROGRESS.totalSections,
      };
    }

    switch (status) {
      case ProjectStatus.CONTEXT_UPLOAD:
        return {
          ...DEFAULT_PROJECT_PROGRESS,
          contextUploaded: true,
        };
      case ProjectStatus.CONTEXT_REVIEW:
      case ProjectStatus.ADR:
        return {
          ...DEFAULT_PROJECT_PROGRESS,
          contextUploaded: true,
          contextReviewed: true,
        };
      case ProjectStatus.GENERATION:
        return {
          ...DEFAULT_PROJECT_PROGRESS,
          contextUploaded: true,
          contextReviewed: true,
          architectureGenerated: true,
        };
      case ProjectStatus.BIZZDESIGN_INTEGRATION:
        return {
          ...DEFAULT_PROJECT_PROGRESS,
          contextUploaded: true,
          contextReviewed: true,
          architectureGenerated: true,
          sectionsCompleted: DEFAULT_PROJECT_PROGRESS.totalSections,
        };
      default:
        return DEFAULT_PROJECT_PROGRESS;
    }
  }
}

export const projectService = new ProjectService();
